import React, { useState } from "react";
import api from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { compressImage } from "@/lib/tps";
import { ImagePlus, X, Sparkles, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

// role: "ref" (general reference, MULTIPLE allowed) | "final_ref" | "payment" | "cake_ready" | "other"
const UNIQUE_ROLES = new Set(["final_ref", "payment", "cake_ready"]);

// Robust file → base64 that never throws on cancelled / unreadable uploads.
async function safeExtractBase64(fileLike) {
  try {
    // Reject empty / cancelled selections up-front
    if (!fileLike) return null;
    // Accept a plain string (data URI / URL) as-is
    if (typeof fileLike === "string") {
      if (!fileLike.trim()) return null;
      return fileLike;
    }
    // Duck-type: needs to look like a Blob/File
    const looksLikeBlob =
      typeof fileLike === "object" &&
      (typeof fileLike.arrayBuffer === "function" ||
        typeof fileLike.stream === "function" ||
        "size" in fileLike);
    if (!looksLikeBlob) return null;
    if (fileLike.size === 0) return null;
    // Only images
    if (fileLike.type && !fileLike.type.startsWith("image/")) return null;
    return await compressImage(fileLike, 1400, 0.82);
  } catch {
    return null;
  }
}

export default function Extract() {
  const [text, setText] = useState("");
  const [imgs, setImgs] = useState([]); // [{b64, role}]
  const [extracted, setExtracted] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const addImage = async (e) => {
    // Handle cancelled picker (no files) without crashing
    const raw = e?.target?.files;
    const files = raw ? Array.from(raw) : [];
    if (files.length === 0) {
      if (e?.target) e.target.value = "";
      return;
    }
    const result = [];
    let failed = 0;
    for (const f of files) {
      const b = await safeExtractBase64(f);
      if (b) result.push({ b64: b, role: "other" });
      else failed++;
    }
    if (result.length > 0) {
      setImgs((arr) => [...arr, ...result]);
      toast.success(`Added ${result.length} image${result.length !== 1 ? "s" : ""}`);
    }
    if (failed > 0) toast.error(`${failed} file${failed !== 1 ? "s" : ""} could not be added (unsupported or empty)`);
    if (e?.target) e.target.value = "";
  };

  // Toggle role for an image. UNIQUE_ROLES clear the same role from other images.
  // General "ref" allows multiple simultaneously.
  const setRole = (idx, role) =>
    setImgs((arr) =>
      arr.map((x, i) => {
        if (i === idx) return { ...x, role: role === x.role ? "other" : role };
        if (UNIQUE_ROLES.has(role) && x.role === role) return { ...x, role: "other" };
        return x;
      }),
    );

  const removeImg = (idx) => setImgs((arr) => arr.filter((_, i) => i !== idx));

  const extract = async () => {
    if (!text.trim()) { toast.error("Paste WhatsApp chat first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/extract/whatsapp", { chat_text: text });
      setExtracted(data.extracted);
      toast.success("Extracted by Claude 4.5");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Extraction failed");
    } finally { setBusy(false); }
  };

  const createOrder = () => {
    if (!extracted) return;
    // Collect images defensively — never assume shape of the sourced data.
    const validImgs = (imgs || []).filter((x) => x && typeof x.b64 === "string" && x.b64);

    const finalRef = validImgs.find((x) => x.role === "final_ref")?.b64 || "";
    const cakeReady = validImgs.find((x) => x.role === "cake_ready")?.b64 || "";
    const pay = validImgs.find((x) => x.role === "payment")?.b64 || "";
    // "ref" role → multiple; everything not otherwise categorised also goes in general refs.
    const generalRefs = validImgs
      .filter((x) => x.role === "ref" || x.role === "other")
      .map((x) => x.b64);

    const payload = {
      customer_name: extracted.customer_name || "",
      customer_phone: extracted.customer_phone || "",
      required_date: extracted.required_date || "",
      required_time: extracted.required_time || "16:00",
      delivery_method: extracted.delivery_method || "Self Pickup",
      delivery_address: extracted.delivery_address || "",
      total_price: Number(extracted.total_price) || 0,
      advance_amount: Number(extracted.advance_amount) || 0,
      notes: extracted.notes || "",
      items: [{
        theme: extracted.theme || "",
        weight: extracted.weight || "",
        tier: extracted.tier || "Single",
        flavour: extracted.flavour || "",
        cake_message: extracted.cake_message || "",
        customization_notes: extracted.customization_notes || "",
      }],
      // IMPORTANT: hand off images into the correct fields
      reference_images: generalRefs,
      reference_image: generalRefs[0] || "",
      final_reference_image: finalRef,
      final_cake_ready_picture: cakeReady,
      payment_screenshot: pay,
    };
    try {
      sessionStorage.setItem("tps_extract_payload", JSON.stringify(payload));
    } catch {
      toast.error("Could not stage images (browser storage full). Try fewer/smaller images.");
      return;
    }
    nav("/orders/new?from=extract");
  };

  const ROLE_OPTIONS = [
    ["ref", "Ref"],
    ["final_ref", "Final"],
    ["payment", "Pay"],
    ["cake_ready", "Cake"],
  ];

  return (
    <div className="space-y-3 animate-fade-in" data-testid="extract-page">
      <h1 className="font-heading text-2xl md:text-3xl">WhatsApp Extract</h1>
      <p className="text-sm text-[#4B5563]">Paste the chat and attach any images shared by the customer. Claude Sonnet 4.5 will draft an order for you. Tag one image as <em>Final</em> reference, one as <em>Pay</em>, one as <em>Cake</em> (cake-ready pic); the rest go into general references. Nothing is saved until you click <strong>Create Order</strong>.</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className="border-[#E8E3DF]">
          <CardContent className="p-4 space-y-3">
            <div className="text-xs uppercase tracking-wider text-[#4B5563]">1. Paste WhatsApp Chat</div>
            <Textarea
              rows={14}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Paste the full chat conversation here..."
              className="font-mono text-xs"
              data-testid="extract-text"
            />
            <div className="text-xs uppercase tracking-wider text-[#4B5563] pt-2">2. Upload Images</div>
            <label className="inline-flex items-center gap-1.5 h-8 px-3 border border-dashed border-[#E8E3DF] rounded-md cursor-pointer hover:bg-[#FCF9F9] text-xs" data-testid="extract-upload-btn">
              <ImagePlus className="h-3.5 w-3.5" /> Add images
              <input type="file" hidden multiple accept="image/*" onChange={addImage} data-testid="extract-file-input" />
            </label>
            {imgs.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {imgs.map((x, i) => (
                  <div key={i} className="relative group">
                    <img src={x.b64} alt="" className="w-full h-24 object-cover rounded-md border border-[#E8E3DF]" data-testid={`extract-img-${i}`} />
                    <button onClick={() => removeImg(i)} className="absolute -top-1 -right-1 bg-white border border-[#E8E3DF] rounded-full p-0.5 shadow" data-testid={`extract-img-remove-${i}`}><X className="h-3 w-3" /></button>
                    <div className="absolute inset-x-0 bottom-0 flex flex-wrap gap-0.5 bg-black/60 p-0.5 rounded-b-md">
                      {ROLE_OPTIONS.map(([k, lbl]) => (
                        <button
                          key={k}
                          onClick={() => setRole(i, k)}
                          data-testid={`extract-role-${k}-${i}`}
                          className={`text-[10px] px-1.5 py-0.5 rounded ${x.role === k ? "bg-[#B86F77] text-white" : "bg-white/80 text-[#141E30]"}`}
                        >{lbl}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="pt-2">
              <Button onClick={extract} disabled={busy || !text.trim()} className="bg-[#B86F77] hover:bg-[#A35D65] h-9 w-full" data-testid="extract-run-btn">
                <Sparkles className="h-4 w-4 mr-1" /> {busy ? "Extracting…" : "Extract with Claude"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#E8E3DF]">
          <CardContent className="p-4 space-y-3">
            <div className="text-xs uppercase tracking-wider text-[#4B5563]">3. Review & Create</div>
            {!extracted ? (
              <div className="text-sm text-[#4B5563] py-8 text-center">Results will appear here.</div>
            ) : (
              <div className="text-sm space-y-1.5" data-testid="extract-result">
                {Object.entries(extracted).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3 border-b border-[#E8E3DF]/60 pb-1.5">
                    <span className="text-xs text-[#4B5563] capitalize">{k.replace(/_/g, " ")}</span>
                    <span className="text-right text-sm">{v?.toString?.() || "—"}</span>
                  </div>
                ))}
                {imgs.length > 0 && (
                  <div className="pt-2 border-t border-[#E8E3DF]" data-testid="extract-image-summary">
                    <div className="text-xs text-[#4B5563] mb-1">Images to be attached to the order:</div>
                    <ul className="text-xs list-disc pl-4 space-y-0.5">
                      <li>Final Reference: <strong>{imgs.filter((x) => x.role === "final_ref").length}</strong></li>
                      <li>Cake Ready: <strong>{imgs.filter((x) => x.role === "cake_ready").length}</strong></li>
                      <li>Payment Screenshot: <strong>{imgs.filter((x) => x.role === "payment").length}</strong></li>
                      <li>General Reference: <strong>{imgs.filter((x) => x.role === "ref" || x.role === "other").length}</strong></li>
                    </ul>
                  </div>
                )}
                <Button onClick={createOrder} className="bg-[#B86F77] hover:bg-[#A35D65] h-9 w-full mt-3" data-testid="extract-create-btn">
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Create Order
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
